import autocomplete from 'autocompleter'
import InputMask from 'inputmask'
import emitter from '../../mixin/emitter'

export default {
  name: 'UiFormInput',

  mixins: [emitter],

  inheritAttrs: false,

  inject: {
    uiFormItem: {
      default: '',
    },
  },

  // language=HTML (for PhpStorm)
  template: `
    <div
      :class="[
        type === 'textarea' ? 'ui-textarea' : 'ui-input',
        {
          'is-inline': inline,
          'is-disabled': disabled,
          'ui-input-group': $slots.prepend || $slots.append,
          'ui-input-group--append': $slots.append,
          'ui-input-group--prepend': $slots.prepend,
        }
      ]"
    >
      <template v-if="type !== 'textarea'">
        <div class="ui-input-group__prepend" v-if="$slots.prepend">
          <slot name="prepend"></slot>
        </div>
        <input
          ref="input"
          class="ui-input__inner"
          :class="inputClass"
          :style="inputStyle"
          v-bind="$attrs"
          :type="type"
          :id="computedId"
          :disabled="disabled"
          :value="currentValue"
          @input="handleInput"
          @change="handleChange"
          @blur="handleBlur"
        />
        <div class="ui-input-group__append" v-if="$slots.append">
          <slot name="append"></slot>
        </div>
      </template>

      <textarea
        v-else
        ref="input"
        class="ui-textarea__inner"
        :class="inputClass"
        :style="inputStyle"
        v-bind="$attrs"
        :id="computedId"
        :disabled="disabled"
        :value="currentValue"
        @input="handleInput"
        @change="handleChange"
        @blur="handleBlur"></textarea>
    </div>
  `,

  props: {
    type: {
      type: String,
      default: 'text',
    },

    id: String,
    disabled: Boolean,
    value: [String, Number],
    inline: {
      type: Boolean,
      default: false,
    },
    inputClass: {
      type: [Array, Object, String],
      default() {
        return []
      },
    },
    inputStyle: {
      type: [Array, Object],
      default() {
        return {}
      },
    },
    mask: {
      type: String,
    },
    maskOpts: {
      type: Object,
      default() {
        return {}
      },
    },
    // [{ value: 'opt1', label: 'Option 1' }]
    selectAutocomplete: {
      type: Array,
      default() {
        return []
      },
    },
  },

  data() {
    return {
      currentValue: null,
    }
  },

  computed: {
    computedId() {
      if (this.id) {
        return this.id
      }

      return this.uiFormItem.computedId
    },

    selectAutocompleteValues() {
      if (!Array.isArray(this.selectAutocomplete)) {
        return []
      }

      return this.selectAutocomplete.map((value) => {
        if (typeof value === 'string') {
          return { label: value, value }
        }
        return value
      })
    },
  },

  watch: {
    /**
     * Responsible for updating internal value when outside value changes.
     */
    value() {
      this.refreshCurrentValue()
    },
  },

  methods: {
    refreshCurrentValue() {
      this.currentValue = this.value === undefined || this.value === null ? '' : this.value
    },

    handleInput(ev) {
      if (this.type === 'file') {
        this.$emit('input', ev.target.files)
      } else {
        const { value } = ev.target
        this.changeValue(value)
      }
    },

    handleChange(ev) {
      if (this.type === 'file') {
        this.$emit('change', ev.target.files)
      } else {
        this.$emit('change', ev.target.value)
      }
    },

    handleBlur(ev) {
      this.$emit('blur', ev)
      this.dispatch('UiFormItem', 'ui.form.blur', [this.currentValue])
    },

    changeValue(value) {
      this.setCurrentValue(value)
      this.$emit('input', value)
    },

    setCurrentValue(value) {
      this.currentValue = value
    },
  },

  created() {
    this.refreshCurrentValue()
  },

  mounted() {
    this.$nextTick(() => {
      if (this.mask) {
        const im = new InputMask(this.mask, this.maskOpts)
        im.mask(this.$refs.input)
      }

      if (this.selectAutocompleteValues.length > 0) {
        autocomplete({
          input: this.$refs.input,
          showOnFocus: true,
          disableAutoSelect: true,
          fetch: (text, update) => {
            if (text === '') {
              update(this.selectAutocompleteValues)
              return
            }
            // eslint-disable-next-line no-param-reassign
            text = text.toLowerCase()
            const suggestions = this.selectAutocompleteValues.filter((n) => n.label.toLowerCase().indexOf(text) !== -1)
            update(suggestions)
          },
          onSelect: (item) => {
            this.changeValue(item.value)
          },
        })
      }
    })
  },
}
